/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-02-16 15:08:51
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, SkeletonBodyText, Button, Stack, Tooltip } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import StrategyDetail from 'src/components/views/merchandising-v2/rule/strategy/strategy-detail';
import ConfirmDeleteModal from 'src/components/views/merchandising-v2/confirm-delete-modal';
import { getStrategyField } from 'src/helpers/merchandising-v2-helper';
import ProductDetail from 'src/components/views/merchandising-v2/rule/strategy/product-detail';
import {
  NUMBER_CONDITION_PER_TYPE,
  STRATEGY_FOR_TAB_PRODUCT
} from 'src/constants/merchandising-v2';

import crownIcon from 'src/assets/images/common/crown.png';
import magicSparklesIcon from 'src/assets/images/common/magic-sparkles-icon.png';
import magicSparkles2Icon from 'src/assets/images/common/magic-sparkles-2.png';

type Props = {
  loading: booleans,
  consequences: Array<Object>
};

type ComposedProps = Props & WithI18nProps;

type State = {
  pinModal: boolean,
  attributesModal: boolean,
  type: string,
  selectedIndexRemove: number,
  deleteModal: boolean
};

class Strategies extends Component<ComposedProps, State> {
  state = {
    pinModal: false,
    attributesModal: false,
    type: 'boost',
    selectedIndexRemove: -1,
    deleteModal: false
  };

  renderButtonStrategies = (type, index, disableButtonStrategy) => {
    const { activeStrategy, enableVSAddon, openAddonModal, i18n } = this.props;
    if (activeStrategy === type) {
      return (
        <Button key={index} primary onClick={() => this.handleOpenStrategy(type, true)}>
          {type === 'productPerformanceRanking' && (
            <img alt="pfs-magic-ai-active-icon" src={magicSparkles2Icon} width={20} height={20} />
          )}
          {i18n.translate(`AdminHTML.MerchandisingV2.Rule.${type}ButtonTitle`)}
        </Button>
      );
    }

    if (type === 'banner' && !enableVSAddon) {
      return (
        <div className="merchandising-lock-banner-btn">
          <Tooltip
            content={
              <div style={{ maxWidth: '220px' }}>
                {i18n.translate('AdminHTML.MerchandisingV2.Rule.bannerButtonTooltip')}
              </div>
            }
            preferredPosition="below">
            <Button key={index} onClick={() => openAddonModal()}>
              {!enableVSAddon && (
                <img alt="pfs-crown-icon" src={crownIcon} width={14} height={12} />
              )}
              {i18n.translate(`AdminHTML.MerchandisingV2.Rule.bannerButtonTitle`)}
            </Button>
          </Tooltip>
        </div>
      );
    }

    return (
      <Button
        disabled={disableButtonStrategy}
        key={index}
        onClick={() => this.handleOpenStrategy(type, true)}>
        {type === 'productPerformanceRanking' && (
          <img alt="pfs-magic-ai-icon" src={magicSparklesIcon} width={20} height={20} />
        )}
        {i18n.translate(`AdminHTML.MerchandisingV2.Rule.${type}ButtonTitle`)}
      </Button>
    );
  };

  invalidDataStrategy = strategy => {
    const { type, attributes = [] } = strategy;
    if (!type || _.isEmpty(type)) return true;

    switch (type) {
      case 'productPerformanceRanking':
        return false;
      case 'pin': {
        const { pinPositions = [] } = strategy;
        const hasValuePin = pinPositions.find(e => !_.isEmpty(e) && !_.isEmpty(e.id));
        return _.isEmpty(hasValuePin);
      }
      case 'banner': {
        const { name, position, imgSrc, link } = strategy;
        return !(name && (position || position === 0) && imgSrc && link);
      }
      default: {
        const emptyAttributes = attributes.filter(e => _.isEmpty(e) || e.value === undefined);
        return !!(emptyAttributes.length === attributes.length);
      }
    }
  };

  getSummaryList = () => {
    const {
      strategies,
      strategyProducts,
      appLanguage,
      optionsList,
      moneyFormat,
      ruleType,
      hasProductPerformanceRanking,
      formValues
    } = this.props;
    // Render list button type of strategy by ruleType
    const isEnablePerformanceRanking = _.get(formValues, 'isEnablePerformanceRanking', false);
    const queries = _.get(formValues, 'queries', []).filter(q => !_.isEmpty(q));
    const productStrategies = Object.keys(strategyProducts)
      .filter(type => strategyProducts[type].some(v => v))
      .map(type => ({
        type,
        isProduct: true,
        products: strategyProducts[type].filter(v => v)
      }));

    const indexStrategies = strategies.map((s, index) => ({ ...s, index }));
    const allStrategies = [...indexStrategies, ...productStrategies];
    this.sortByTypeStrategy(allStrategies);

    if (isEnablePerformanceRanking && hasProductPerformanceRanking && queries.length > 0) {
      allStrategies.push({
        type: 'productPerformanceRanking',
        queries,
        index: allStrategies.length
      });
    }

    const summaryList = _.map(allStrategies, strategy => {
      const { type = '', isProduct, products = [], index } = strategy;
      // 1 - View detail Product
      if (isProduct && products.length > 0) {
        return (
          <ProductDetail
            key={`product-detail-${index}`}
            type={type}
            products={products}
            handleEditAttribute={this.handleEditProduct}
            handleDelete={this.toggleDeleteModal}
            moneyFormat={moneyFormat}
          />
        );
      }
      // 2 - View detail Attribute
      // check invalid data
      if (this.invalidDataStrategy(strategy)) return null;

      // render valid data
      return (
        <StrategyDetail
          key={index}
          strategy={strategy}
          index={index}
          handleEditPin={this.handleEditPin}
          handleEditAttribute={this.handleEditAttribute}
          handleEditProductPerformanceRanking={this.handleEditProductPerformanceRanking}
          handleDelete={this.toggleDeleteModal}
          appLanguage={appLanguage}
          optionsList={optionsList}
          moneyFormat={moneyFormat}
          ruleType={ruleType}
        />
      );
    }).filter(s => s);

    return summaryList;
  };

  render() {
    const { i18n, loading, ruleType, formValues, hasProductPerformanceRanking } = this.props;
    const { deleteModal } = this.state;
    // Render list button type of strategy by ruleType
    const { collection: collectionData, queriesType = 'specific' } = formValues || {};
    const strategyTypeList = getStrategyField(ruleType, queriesType);

    const disableButtonStrategy =
      queriesType !== 'all' &&
      ruleType === 'collection' &&
      (!collectionData || _.isEmpty(collectionData));

    const summaryList = this.getSummaryList();
    return (
      <React.Fragment>
        {loading ? (
          <FormLayout>
            <SkeletonBodyText lines={5} />
          </FormLayout>
        ) : (
          <>
            <div className="conditions">
              {strategyTypeList
                .map((e, index) => {
                  if (e === 'productPerformanceRanking' && !hasProductPerformanceRanking)
                    return null;
                  return (
                    <div
                      className={`strategy-btn ${
                        e === 'productPerformanceRanking' ? 'product-performance-ranking-btn' : ''
                      }`}>
                      {this.renderButtonStrategies(e, index, disableButtonStrategy)}
                    </div>
                  );
                })
                .filter(btn => btn)}
              {disableButtonStrategy && (
                <p className="mt-5" style={{ color: '#f2711c' }}>
                  {i18n.translate('AdminHTML.MerchandisingV2.Rule.notifyWarningSelectCollection')}
                </p>
              )}
            </div>
            <div style={{ marginTop: '25px', marginBottom: '0.625rem' }}>
              <Stack vertical>
                {summaryList.length > 0 && (
                  <div className="summary-title set-rule-strategies-title">
                    <span className="title">
                      {i18n.translate('AdminHTML.MerchandisingV2.Rule.summary_Title')}
                    </span>
                  </div>
                )}
                {summaryList.map(s => s)}
              </Stack>
            </div>
          </>
        )}

        <ConfirmDeleteModal
          active={deleteModal}
          handleChange={() => this.toggleModal('deleteModal')}
          handleRemove={this.handleDelete}
          title={i18n.translate('AdminHTML.MerchandisingV2.Rule.consequence_Delete_Title')}
          content={i18n.translate('AdminHTML.MerchandisingV2.Rule.consequence_Delete_Content')}
          subContent={i18n.translate(
            'AdminHTML.MerchandisingV2.Rule.consequence_Delete_SubContent'
          )}
        />
      </React.Fragment>
    );
  }

  // Toggle modal
  toggleModal = (fieldName: string) => {
    const value = this.state[fieldName];
    if (value) {
      this.setState({ [fieldName]: !value, selectedIndexRemove: -1, type: 'boost' });
    } else {
      this.setState({ [fieldName]: !value });
    }
  };

  // Handle action edit on pin edit button
  handleEditPin = (index: number) => {
    this.props.handleOpenStrategyCard('pin', true);
  };

  // Handle action edit on pin edit button
  handleEditProductPerformanceRanking = (index: number) => {
    this.props.handleOpenStrategyCard('productPerformanceRanking', true);
  };

  // Handle action edit on hide/demote/boost edit button
  handleEditAttribute = (index: number) => {
    const { strategies, selectedIndex, activeStrategy, strategyTab } = this.props;
    const strategy = strategies[index] || {};

    if (activeStrategy === strategy.type) {
      // Change edit
      if (strategies.toString() !== selectedIndex && strategyTab === 1) {
        this.props.setSelected(JSON.stringify(strategy));
      } else {
        // Re press edit icon => close detail modal
        this.props.handleOpenStrategyCard(strategy.type, true);
      }
    } else {
      this.props.setSelected(JSON.stringify(strategy));
      this.props.handleOpenStrategyCard(strategy.type, true);
    }
  };

  handleEditProduct = (type: string) => {
    this.props.handleOpenStrategyCard(type, true, 'product');
  };

  // Handle toggle delete modal
  toggleDeleteModal = (index: number) => {
    this.setState({ selectedIndexRemove: index, deleteModal: true });
  };

  // Handle open strategy
  handleOpenStrategy = (type, removeEmptyStrategies) => {
    const { handleOpenStrategyCard, setSelected } = this.props;
    handleOpenStrategyCard(type, removeEmptyStrategies);
    setSelected(-1);
  };

  handleDeleteProduct = type => {
    const { strategyProducts, updateField } = this.props;
    const newProducts = {
      ...strategyProducts,
      [type]: Array.from(Array(NUMBER_CONDITION_PER_TYPE[type] || 3).keys()).map(_ => '')
    };
    updateField('products', newProducts);
  };

  // Handle action delete consequence item
  handleDelete = () => {
    const { strategies, fields, renderPreview } = this.props;
    const { selectedIndexRemove } = this.state;
    if (STRATEGY_FOR_TAB_PRODUCT.includes(selectedIndexRemove)) {
      this.handleDeleteProduct(selectedIndexRemove);
    } else {
      fields.remove(selectedIndexRemove);
      this.props.setSelected(-1);

      if (_.get(strategies, `[${selectedIndexRemove}].type`, '') === 'pin' || fields.length < 1) {
        this.props.handleOpenStrategyCard('');
      }
    }
    this.toggleModal('deleteModal');

    // Render preview
    renderPreview('consequences/index handleDelete()');
  };

  // Sort consequences in the order of Pin - Boost - Demote - Hide
  sortByTypeStrategy = (strategies: Array<Object>) => {
    const objPosition = {
      pin: 1,
      banner: 2,
      boost: 3,
      demote: 4,
      hide: 5,
      filter: 6
    };
    strategies.sort((a, b) => {
      const positionA = objPosition[a.type];
      const positionB = objPosition[b.type];
      return positionA - positionB;
    });
  };
}

export default withI18n()(Strategies);
